import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useI18nContext } from '@/i18n/i18n-react';
import type { TranslationFunctions } from '@/i18n/i18n-types';
import { formatShortDateTime } from '@/lib/utils';

export type IncidentStatus = 'resolved' | 'investigating' | 'monitoring';

export type IncidentSummaryProps = {
  summary: string;
  status: IncidentStatus;
  startedAt: string;
  resolvedAt?: string;
};

function statusBadgeVariant(status: IncidentStatus) {
  switch (status) {
    case 'resolved':
      return 'secondary' as const;
    case 'investigating':
      return 'destructive' as const;
    case 'monitoring':
      return 'default' as const;
  }
}

function getStatusLabel(
  status: IncidentStatus,
  LL: TranslationFunctions
): string {
  switch (status) {
    case 'resolved':
      return LL.incidentPage.statusValues.resolved();
    case 'investigating':
      return LL.incidentPage.statusValues.investigating();
    case 'monitoring':
      return LL.incidentPage.statusValues.monitoring();
  }
}

export default function IncidentSummary({
  summary,
  status,
  startedAt,
  resolvedAt,
}: IncidentSummaryProps) {
  const { LL, locale } = useI18nContext();

  const statusLabel = getStatusLabel(status, LL);

  return (
    <div className="not-prose mb-6">
      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {LL.incidentPage.status()}
            </span>
            <Badge variant={statusBadgeVariant(status)}>{statusLabel}</Badge>
          </div>
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-muted-foreground">
                {LL.incidentPage.startedAt()}
              </dt>
              <dd className="font-medium">
                {formatShortDateTime(startedAt, locale)}
              </dd>
            </div>
            {resolvedAt ? (
              <div>
                <dt className="text-muted-foreground">
                  {LL.incidentPage.resolvedAt()}
                </dt>
                <dd className="font-medium">
                  {formatShortDateTime(resolvedAt, locale)}
                </dd>
              </div>
            ) : null}
          </dl>
          <p className="text-muted-foreground">{summary}</p>
        </CardContent>
      </Card>
    </div>
  );
}
